import { GiPadlock } from "react-icons/gi";
import { ResourcePool } from "../types/castle";
import { CurrencyIcon } from "./CurrencyIcon";
import { formatNumber } from "../lib/format";
import { useCountUp, useFlashClass } from "../lib/animations";

interface Props {
  resources: ResourcePool;
  safeStorage?: ResourcePool;
}

const tiles: { key: keyof ResourcePool; kind: "gold" | "wood" | "stone" | "mana" }[] = [
  { key: "gold", kind: "gold" },
  { key: "wood", kind: "wood" },
  { key: "stone", kind: "stone" },
  { key: "mana", kind: "mana" },
];

export function ResourceBar({ resources, safeStorage }: Props) {
  return (
    <div
      data-tutorial="resource-bar"
      className="grid grid-cols-4 gap-1 border-b border-tg-secondaryBg bg-tg-headerBg px-2 py-2 text-sm"
    >
      {tiles.map(({ key, kind }) => (
        <ResourceTile
          key={key}
          kind={kind}
          value={resources[key]}
          safe={safeStorage?.[key] ?? 0}
          size={20}
        />
      ))}
    </div>
  );
}

// One resource cell. Number animates smoothly from previous value (useCountUp)
// and gets a brief scale/glow pulse on every increase (useFlashClass +
// value-pop keyframe). Decreases happen without the pulse — losing resources
// shouldn't feel celebratory.
//
// Below the main number, a small lock icon + protected amount shows what's
// safe from PvP raids (Warehouse safe-storage). Hidden when safe = 0.
function ResourceTile({
  kind,
  value,
  safe,
  size,
}: {
  kind: "gold" | "wood" | "stone" | "mana";
  value: number;
  safe: number;
  size: number;
}) {
  const display = useCountUp(value, 500);
  // useFlashClass triggers on every change. We bucket-down to whole units of
  // change so micro-fluctuations (e.g. production ticks) don't constantly
  // strobe. Round to nearest 50 — only meaningful jumps pulse.
  const pulseTrigger = Math.floor(value / 50);
  const wasIncrease = display < value;
  const { className: pulseClass } = useFlashClass(
    wasIncrease ? pulseTrigger : "static",
    "animate-value-pop",
    560,
  );
  // Protected portion: shown as a sub-line under the number. Capped at the
  // current value so we never display "protected 100M" when player has 50M.
  const protectedShown = Math.min(safe, value);

  return (
    <span className="flex flex-col">
      <span className="flex items-center gap-1.5">
        <CurrencyIcon kind={kind} size={size} />
        <span className={`tabular-nums inline-block ${pulseClass}`}>
          {formatNumber(display)}
        </span>
      </span>
      {protectedShown > 0 && (
        <span
          className="ml-[26px] flex items-center gap-0.5 text-[10px] leading-tight text-tg-hint tabular-nums"
          title="Защищено от рейдов уровнем Хранилища"
        >
          <GiPadlock size={10} />
          {formatNumber(protectedShown)}
        </span>
      )}
    </span>
  );
}
